import React, { useEffect, useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { Modal, Button, Spinner, Form as RBForm } from "react-bootstrap";
import { FaEdit, FaTrash, FaEye, FaRedo } from "react-icons/fa";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "../../utils/AxiosInstance";
import { useNavigate } from "react-router-dom";

const CategoryManagement = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editData, setEditData] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const navigate = useNavigate();

  // Pagination states

  const [page, setPage] = useState(1);
  const [limit] = useState(5);
  const [totalPages, setTotalPages] = useState(1);

  // Search and filter

  const [search, setSearch] = useState("");
  const [isActiveFilter, setIsActiveFilter] = useState("");

  const getCategoryData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");

      const res = await axios.get(
        `/admin/category/getall?page=${page}&limit=${limit}&search=${search}&isActive=${isActiveFilter}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      // console.log(res);
      const { data } = res.data;
      // console.log(data.data);
      // console.log(data.totalPages);

      if (res.status === 200) {
        setCategories(data.data);
        setTotalPages(data.totalPages);
      }
    } catch (error) {
      console.log("Fetch error:", error);
      toast.error("Error while fetching categories");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getCategoryData();
  }, [page, search, isActiveFilter]);

  const handleAddModal = () => {
    setEditData(null);
    setEditMode(false);
    setShowModal(true);
  };

  const handleEdit = (cat) => {
    setEditData(cat);
    setEditMode(true);
    setShowModal(true);
  };

  const handleDeleteClick = (id) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    setIsSubmitted(true);
    const token = localStorage.getItem("token");

    try {
      const res = await axios.delete(`/admin/category/remove/${deleteId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (res.status === 200) {
        setCategories((prev) => prev.filter((cat) => cat._id !== deleteId));

        toast.success(res.data.message);
      }
    } catch (error) {
      toast.error("Error while deleting category");
    } finally {
      setIsSubmitted(false);
      setShowDeleteModal(false);
    }
  };

  const validationSchema = Yup.object({
    name: Yup.string().required("Name is required").min(5).max(20),
    description: Yup.string()
      .required("Description is required")
      .min(10)
      .max(50),
    isActive: Yup.boolean().required("Status is required"),
  });

  const handleSubmit = async (values, { setSubmitting }) => {
    setIsSubmitted(true);
    const token = localStorage.getItem("token");

    try {
      let response;
      if (editMode) {
        response = await axios.put(
          `/admin/category/update/${editData._id}`,
          values,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        console.log(response.data.data);

        toast.success(response.data.message);

        // getCategoryData();
        setCategories((prev) =>
          prev.map((cat) =>
            cat._id === response.data.data._id ? response.data.data : cat
          )
        );
      } else {
        response = await axios.post("/admin/category/add", values, {
          headers: { Authorization: `Bearer ${token}` },
        });
        // console.log(response.data.data);
        setCategories((prev) => [response.data.data, ...prev]);

        toast.success(response.data.message);
      }

      setShowModal(false);
    } catch (error) {
      toast.error("Error while saving category");
    } finally {
      setIsSubmitted(false);
      setSubmitting(false);
    }
  };

  //  -----------------pagination--------------

  const pages = [];

  if (totalPages <= 7) {
    for (let i = 1; i <= totalPages; i++) pages.push(i);
  } else {
    pages.push(1);

    if (page > 3) pages.push("...");

    let startPage = Math.max(2, page - 1);
    let endPage = Math.min(totalPages - 1, page + 1);

    for (let i = startPage; i <= endPage; i++) pages.push(i);

    if (page < totalPages - 2) pages.push("...");

    pages.push(totalPages);
  }

  const handleChange = (pageNumber) => {
    setPage(pageNumber);
  };

  const handleSearch = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };
  const handleReset = () => {
    getCategoryData();
    setSearch("");
  };

  const handleSelectSearch = (e) => {
    setIsActiveFilter(e.target.value);
    setPage(1);
  };

  const handlePrevChange = () => {
    if (page > 1) setPage((prev) => prev - 1);
  };

  const handleNextChange = () => {
    if (page < totalPages) setPage((prev) => prev + 1);
  };

  // console.log(search);
  // console.log(page);

  const disabledPrev = page === 1;
  const disabledNext = page === totalPages;

  return (
    <>
      <div className="container mt-4">
        <h4 className="text-center mb-4">Admin Panel - Category Management</h4>

        <div className="d-flex justify-content-between mb-3">
          <div className="d-flex gap-2">
            <input
              type="text"
              placeholder="Search by name"
              className="form-control"
              value={search}
              onChange={(e) => handleSearch(e)}
            />
            <Button
              variant="warning"
              size="sm"
              className="me-2 px-4"
              onClick={() => handleReset()}
            >
              <FaRedo />
            </Button>

            <select
              value={isActiveFilter}
              onChange={(e) => handleSelectSearch(e)}
            >
              <option value="">All Status</option>
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>
          </div>
          <Button variant="success" onClick={handleAddModal}>
            Add Category
          </Button>
        </div>

        {loading ? (
          <div className="text-center my-4">
            <Spinner animation="border" />
          </div>
        ) : categories.length === 0 ? (
          <p className="text-center">No category found.</p>
        ) : (
          <>
            <table className="table table-bordered">
              <thead className="table-dark">
                <tr>
                  <th>No</th>
                  <th>Name</th>
                  <th>Description</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {categories.map((cat, index) => (
                  <tr key={cat._id}>
                    <td>{(page - 1) * limit + index + 1}</td>

                    <td>{cat.name}</td>
                    <td>{cat.description}</td>
                    <td>{cat.isActive ? "Active" : "Inactive"}</td>
                    <td className="d-flex">
                      <Button
                        variant="warning"
                        size="sm"
                        className="me-2"
                        onClick={() => handleEdit(cat)}
                      >
                        <FaEdit />
                      </Button>
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => handleDeleteClick(cat._id)}
                      >
                        <FaTrash />
                      </Button>

                      <Button
                        variant="info"
                        size="sm"
                        className="ms-2"
                        onClick={() =>
                          navigate(
                            `/admin-dashboard/category/details/${cat._id}`
                          )
                        }
                      >
                        <FaEye />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}

        <ToastContainer />

        <div className="d-flex justify-content-center gap-2 my-5">
          <button
            className="btn btn-success"
            onClick={handlePrevChange}
            disabled={disabledPrev}
          >
            Prev
          </button>

          {pages.map((pg, i) => (
            <button
              key={i}
              disabled={pg === "..."}
              onClick={() => typeof pg === "number" && handleChange(pg)}
              className={`btn ${
                pg === page
                  ? "btn-dark"
                  : pg === "..."
                  ? "btn-secondary"
                  : "btn-light"
              } rounded-0`}
            >
              {pg}
            </button>
          ))}

          <button
            className="btn btn-success"
            onClick={handleNextChange}
            disabled={disabledNext}
          >
            Next
          </button>
        </div>

        {/* Add/Edit Modal */}

        <Modal
          show={showModal}
          onHide={() => setShowModal(false)}
          backdrop="static"
        >
          <Modal.Header closeButton>
            <Modal.Title>
              {editMode ? "Update Category" : "Add Category"}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Formik
              initialValues={{
                name: editData?.name || "",
                description: editData?.description || "",
                isActive: editData?.isActive ?? true,
              }}
              enableReinitialize
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
            >
              {({ isSubmitting, values, setFieldValue }) => (
                <Form>
                  <div className="mb-2">
                    <label>Name</label>
                    <Field name="name" className="form-control" />
                    <ErrorMessage
                      name="name"
                      component="div"
                      className="text-danger small"
                    />
                  </div>

                  <div className="mb-2">
                    <label>Description</label>
                    <Field
                      as="textarea"
                      name="description"
                      className="form-control"
                      rows={3}
                    />
                    <ErrorMessage
                      name="description"
                      component="div"
                      className="text-danger small"
                    />
                  </div>

                  <div className="form-check mb-2">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="isActive"
                      checked={values.isActive}
                      onChange={() =>
                        setFieldValue("isActive", !values.isActive)
                      }
                    />
                    <label className="form-check-label" htmlFor="isActive">
                      Active
                    </label>
                  </div>

                  <div className="text-end mt-3">
                    <Button
                      variant="secondary"
                      onClick={() => setShowModal(false)}
                      className="me-2"
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="success"
                      type="submit"
                      disabled={isSubmitting}
                    >
                      {isSubmitting
                        ? "Saving..."
                        : editMode
                        ? "Update"
                        : "Create"}
                    </Button>
                  </div>
                </Form>
              )}
            </Formik>
          </Modal.Body>
        </Modal>

        {/* Delete Modal */}

        <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
          <Modal.Header closeButton>
            <Modal.Title>Confirm Delete</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            Are you sure you want to delete this category?
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => setShowDeleteModal(false)}
            >
              Cancel
            </Button>
            <Button variant="danger" onClick={handleDeleteConfirm}>
              {isSubmitted ? "Deleting..." : "Delete"}
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </>
  );
};

export default CategoryManagement;
